import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { Box, CircularProgress, TextField } from '@mui/material'
import { videoLessonApiSlice } from '../../../../features/section/videoLessonSlice'
import { toMinutesAndSeconds } from '../../../../utils/functions'
import { ui } from '../../../../utils/uiTokens'
import { filterField } from '../../../../components/filterSelect/FilterSelect'

interface Props {
    videoLessonId: number
    studentId: number
}

const toYmd = (d: Date) => d.toISOString().slice(0, 10)

// Kinescope treats `to` as an exclusive midnight boundary, so to include the
// selected end day we send the next day (from=21 to=21 => sent to=22).
const addDay = (ymd: string) => {
    const d = new Date(ymd + 'T00:00:00Z')
    d.setUTCDate(d.getUTCDate() + 1)
    return d.toISOString().slice(0, 10)
}

const KinescopeVideoAnalysis: React.FC<Props> = ({
    videoLessonId,
    studentId,
}) => {
    const { formatMessage } = useIntl()

    const [from, setFrom] = useState(() => {
        const d = new Date()
        d.setDate(d.getDate() - 30)
        return toYmd(d)
    })
    const [to, setTo] = useState(() => toYmd(new Date()))

    const { data, isFetching } =
        videoLessonApiSlice.useGetStudentVideoKinescopeAnalysisQuery({
            videoLessonId,
            studentId,
            from,
            to: addDay(to),
        })

    // not a Kinescope video: render nothing, the legacy panel already covers it
    if (data && !data.kinescope) {
        return null
    }

    const styles = {
        wrapper: {
            mt: 1.5,
            ml: '48px',
            p: 2,
            borderRadius: '12px',
            border: `1px solid ${ui.border}`,
            background: ui.surface,
        },
        title: {
            fontSize: '12px',
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: ui.muted,
            mb: 1.5,
        },
        filters: {
            display: 'flex',
            gap: 2,
            mb: 2,
            flexWrap: 'wrap',
        },
        totals: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
            gap: 2,
            mb: 2,
        },
        metric: {
            p: 1.5,
            borderRadius: '10px',
            border: `1px solid ${ui.border}`,
            background: ui.card,
        },
        metricLabel: {
            fontSize: '12px',
            color: ui.muted,
        },
        metricValue: {
            fontSize: '18px',
            fontWeight: 600,
            color: ui.ink,
        },
        timelineRow: {
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '13px',
            py: 0.75,
            borderTop: `1px solid ${ui.border}`,
        },
    }

    const total = data?.total

    const metrics: { id: string; value: string | number }[] = [
        {
            id: 'kinescopeWatchTime',
            value: toMinutesAndSeconds(total?.watchTime || 0),
        },
        { id: 'kinescopeViews', value: total?.view || 0 },
        { id: 'kinescopeUniqueViews', value: total?.uniqueView || 0 },
        { id: 'kinescopePlays', value: total?.play || 0 },
        { id: 'kinescopePauses', value: total?.pause || 0 },
        { id: 'kinescopeSeeks', value: total?.seek || 0 },
        { id: 'kinescopeReplays', value: total?.replay || 0 },
    ]

    return (
        <Box sx={styles.wrapper}>
            <Box sx={styles.title}>
                {formatMessage({ id: 'kinescopeAnalytics' })}
            </Box>

            <Box sx={styles.filters}>
                <TextField
                    type="date"
                    size="small"
                    label={formatMessage({ id: 'dateFrom' })}
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ sx: filterField }}
                />
                <TextField
                    type="date"
                    size="small"
                    label={formatMessage({ id: 'dateTo' })}
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ sx: filterField }}
                />
            </Box>

            {isFetching ? (
                <CircularProgress sx={{ m: 1 }} />
            ) : (
                <>
                    <Box sx={styles.totals}>
                        {metrics.map((m) => (
                            <Box
                                key={m.id}
                                sx={styles.metric}
                            >
                                <Box sx={styles.metricLabel}>
                                    {formatMessage({ id: m.id })}
                                </Box>
                                <Box sx={styles.metricValue}>{m.value}</Box>
                            </Box>
                        ))}
                    </Box>

                    {data?.timeline?.length ? (
                        <Box>
                            {data.timeline.map((day) => (
                                <Box
                                    key={day.date}
                                    sx={styles.timelineRow}
                                >
                                    <Box>{day.date.slice(0, 10)}</Box>
                                    <Box>
                                        {toMinutesAndSeconds(
                                            day.metrics.watchTime || 0,
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    ) : null}
                </>
            )}
        </Box>
    )
}

export default KinescopeVideoAnalysis
